import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import escapeRegExp from 'lodash/escapeRegExp'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import InlineSearch from 'modules/core/components/InlineSearch'
import Text from 'modules/core/components/Inputs/Text'
import Popover from 'modules/core/components/Popover'
import ScrollableItem from 'modules/core/components/ScrollableItem'
import ScrollableList from 'modules/core/components/ScrollableList'
import { normalizeText } from 'modules/core/lib/stringUtils'

import SelectOption from './SelectOption'

interface Props {
  label: string
  secondaryLabel?: React.ReactNode | React.ReactNode[]
  field: Field
  autoclose?: boolean
  readonly?: boolean
  autofilled?: boolean
  options: Array<{
    label: string
    value: string
    subtitle?: string
  }>
  onChange?: (field: Field) => void
}

const FilterSelect = ({
  label,
  secondaryLabel,
  field,
  autoclose,
  options,
  readonly = false,
  autofilled = false,
  onChange,
}: Props) => {
  const [focused, toggleFocus] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')

  const input = (
    <Text
      label={label}
      secondaryLabel={secondaryLabel}
      field={field}
      onFocus={() => toggleFocus(true)}
      select
      readonly={readonly}
      autofilled={autofilled}
    />
  )

  if (!focused) {
    return input
  }

  const filteredItems = options
    .filter((option) => {
      const regex = new RegExp(`.*${escapeRegExp(normalizeText(searchTerm))}.*`, 'i')

      return (
        searchTerm === '' ||
        normalizeText(option.label).match(regex) ||
        (option.subtitle && normalizeText(option.subtitle).match(regex))
      )
    })
    .map((item) => {
      return (
        <ScrollableItem key={item.value}>
          <SelectOption
            label={item.label}
            subtitle={item.subtitle}
            selected={item.value === field.value}
            onClick={() => {
              field.set(item.value)
              onChange?.(field)
              autoclose && toggleFocus(false)
            }}
          />
        </ScrollableItem>
      )
    })

  return (
    <Popover
      attachment="bottom center"
      toggle={(value: boolean) => {
        setSearchTerm('')

        return () => toggleFocus(value)
      }}
      open={focused}
      size="big"
    >
      {input}
      <Box flexDirection="column" padding="s20">
        <BoxList>
          <InlineSearch
            autofocus={false}
            onSearch={(event) => setSearchTerm(event.target.value)}
            search={searchTerm}
          />
          <ScrollableList>{filteredItems}</ScrollableList>
        </BoxList>
      </Box>
    </Popover>
  )
} // FIXME: I'm not sure that this component should depend
// on factorial-forms, that's why it needs observer decorator

export default observer(FilterSelect)
